import React, { useEffect, useCallback } from 'react';
import { FiAlertTriangle, FiTrash2, FiHelpCircle } from 'react-icons/fi';
import './ConfirmModal.css';


/**
 * ConfirmModal — Premium Yes / No confirmation dialog.
 *
 * Usage (standalone):
 *   <ConfirmModal
 *     open={showDeleteDialog}
 *     variant="danger"
 *     title="Delete Teacher?"
 *     message="This action cannot be undone."
 *     confirmLabel="Delete"
 *     onConfirm={() => handleDelete(id)}
 *     onCancel={() => setShowDeleteDialog(false)}
 *   />
 *
 * More typically it is driven via the useModal() hook — see ModalContext.jsx
 */
const iconMap = {
  info: <FiHelpCircle />,
  warning: <FiAlertTriangle />,
  danger: <FiTrash2 />,
};

const ConfirmModal = ({
  open = false,
  variant = 'info', // 'info' | 'warning' | 'danger'
  title = 'Are you sure?',
  message = '',
  confirmLabel = 'Confirm',
  cancelLabel = 'Cancel',
  showInput = false,
  inputPlaceholder = 'Type here...',
  inputValue = '',
  setInputValue,
  icon = null,
  onConfirm,
  onSecondary,
  onCancel,
  loading = false,
  secondaryLabel = '',
}) => {
  // Close on Escape
  const handleKeyDown = useCallback(
    (e) => {
      if (e.key === 'Escape' && open && !loading) onCancel?.();
      if (e.key === 'Enter' && open && !loading && !showInput) onConfirm?.();
    },
    [open, loading, onCancel, onConfirm, showInput]
  );


  useEffect(() => {
    document.addEventListener('keydown', handleKeyDown);
    return () => document.removeEventListener('keydown', handleKeyDown);
  }, [handleKeyDown]);

  // Prevent body scroll while open
  useEffect(() => {
    if (open) document.body.style.overflow = 'hidden';
    else document.body.style.overflow = '';
    return () => {
      document.body.style.overflow = '';
    };
  }, [open]);

  return (
    <div
      className={`confirm-modal-overlay${open ? ' open' : ''}`}
      onClick={(e) => {
        if (e.target === e.currentTarget && !loading) onCancel?.();
      }}
      role="dialog"
      aria-modal="true"
      aria-labelledby="confirm-modal-title"
      id="confirm-modal"
    >
      <div className="confirm-modal-card">
        {/* Icon */}
        <div className={`confirm-modal-icon ${variant}`}>
          {icon ? icon : iconMap[variant] || iconMap.info}
        </div>

        {/* Title */}
        <h2 className="confirm-modal-title" id="confirm-modal-title">
          {title}
        </h2>

        {/* Message */}
        {message && <p className="confirm-modal-message">{message}</p>}

        {/* Dynamic Input */}
        {showInput && (
          <div className="confirm-modal-input-wp">
            <textarea
              className="confirm-modal-textarea"
              placeholder={inputPlaceholder}
              value={inputValue}
              onChange={(e) => setInputValue(e.target.value)}
              autoFocus
            />
          </div>
        )}

        {/* Actions */}
        <div className={`confirm-modal-actions ${secondaryLabel ? 'has-secondary' : ''}`}>
          <button
            type="button"
            className="confirm-modal-btn cancel"
            onClick={onCancel}
            disabled={loading}
          >
            {cancelLabel}
          </button>
          
          {secondaryLabel && (
            <button
              type="button"
              className="confirm-modal-btn secondary"
              onClick={onSecondary}
              disabled={loading}
            >
              {secondaryLabel}
            </button>
          )}

          <button
            type="button"
            className={`confirm-modal-btn confirm ${variant === 'danger' ? 'danger' : ''} ${showInput && !inputValue.trim() ? 'disabled' : ''}`}
            onClick={onConfirm}
            disabled={loading || (showInput && !inputValue.trim())}
          >
            {loading ? 'Please wait…' : confirmLabel}
          </button>
        </div>
      </div>
    </div>
  );
};

export default ConfirmModal;
